import React from 'react'
import { useTranslation } from 'react-i18next'
import { match } from 'ts-pattern'

import {
  compile,
  createBreadcrumbWithI18n,
} from '~/apps/ninesegsAnalyzer/routes/paths'
import {
  PageContainer,
  type PageContainerProps,
} from '~/components/elements/PageContainer'
import { ErrorPanel } from '~/components/features/ErrorPanel'
import { useLogEvent } from '~/hooks/useLogEvent'
import { useNotify } from '~/hooks/useNotify'
import {
  useInvalidateSurveyForSettingsQuery,
  useSurveyForSettingsQueryQuestions,
} from '~/hooks/useSurveyForSettingsQuery'

import { useBrandGroupId } from '../hooks/useBrandGroupId'
import { useSurveyId } from '../hooks/useSurveyId'
import { useUpdateBrandGroupMutation } from '../hooks/useUpdateBrandGroupMutation'
import { pathKey } from '../utils'

import { SegmentDefinitionsInput } from './SegmentDefinitionsInput'

export const SegmentDefinitions = () => {
  const surveyId = useSurveyId()
  const brandGroupId = useBrandGroupId()
  const { t } = useTranslation()

  const createBreadcrumb = React.useMemo(() => createBreadcrumbWithI18n(t), [t])
  const breadcrumbs = React.useMemo<PageContainerProps['breadcrumbs']>(
    () => [
      createBreadcrumb('top', {}),
      createBreadcrumb('settings'),
      createBreadcrumb('brandGroupList', { surveyId }),
      createBreadcrumb(pathKey, { surveyId, brandGroupId }),
    ],
    [brandGroupId, createBreadcrumb, surveyId],
  )

  const questions = useSurveyForSettingsQueryQuestions()

  const logEvent = useLogEvent()
  const notify = useNotify()
  const invalidateSurveyForSettingsQuery = useInvalidateSurveyForSettingsQuery()
  const updateBrandGroupMutation = useUpdateBrandGroupMutation({
    onSuccess: () => {
      logEvent('Define 9segs criteria')
      notify({ content: t('saved'), style: 'success' })
      invalidateSurveyForSettingsQuery()
    },
  })

  return (
    <PageContainer
      breadcrumbs={breadcrumbs}
      executing={updateBrandGroupMutation.isPending}
    >
      {match(questions.length)
        .with(0, () => (
          <ErrorPanel
            backLinkLabel={t('data_upload')}
            backLinkPath={compile('dataSettings', { surveyId })}
            message={t('questionnaire_data_does_not_exist')}
          />
        ))
        .otherwise(() => (
          <SegmentDefinitionsInput onSave={updateBrandGroupMutation.mutate} />
        ))}
    </PageContainer>
  )
}
